import * as THREE from 'three';
import type { Hero } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { clear, h, setStyle } from '../ui/dom.ts';
import { nameChip, type Legacy } from '../ui/name-chip.ts';
import type { TagBox } from './mark-pin.ts';

/** How long a chat line stays over the speaker's head. */
const SAY_MS = 6000;

/**
 * A small name over each hero's miniature, above its health bar: yours highlighted, ghosts and
 * hired hands in a quieter tone. They shrink as the camera pulls back, and when two overlap the
 * nearer one wins, so a crowd stays readable on a phone. A chat line shows a few seconds in a bubble
 * over the name (a speaking hero's name is never the one hidden). A hero with its path's legacy wears its plate
 * (ui/name-chip.ts): close up with its title, pulled back only the emblem and the name; when tags clash, yours wins,
 * then whoever is talking, then a legacy's (docs/SENDAS.md, «La etiqueta con el nombre»).
 */
export class NameTags {
  readonly el = h('div.name-tags');
  private tags = new Map<string, HTMLElement>();
  private said = new Map<string, { text: string; until: number }>();
  /** What each tag shows now (its name and legacy), to rebuild it only when that changes. */
  private shown = new Map<string, string>();
  private v = new THREE.Vector3();
  /** Where the tags shown stand on screen this frame (px): the paths' marks keep off them (game/mark-pin.ts). */
  readonly boxes: TagBox[] = [];

  /** Both are read when placing (the screens that own this aren't built yet when it is). */
  constructor(
    private me: () => string,
    private names: () => Record<string, string>,
    private legacy: (hero: Hero) => Legacy | null = () => null,
  ) {}

  /** `pid` said `text`: over their head for a few seconds, if their hero is on the field (true then). */
  say(pid: string, text: string, heroes: Hero[]): boolean {
    if (!heroes.some((x) => x.pid === pid && x.downTicks <= 0)) return false;
    this.said.set(pid, { text, until: performance.now() + SAY_MS });
    return true;
  }

  private label(hero: Hero): string {
    if (hero.control === 'merc') return t('party.mercTag', { cls: t(`class.${hero.cls}`) });
    return this.names()[hero.pid] || hero.name || t(`class.${hero.cls}`);
  }

  place(heroes: Hero[], positions: Map<number, THREE.Vector3>, camera: THREE.Camera, w: number, ht: number): void {
    const seen = new Set<string>();
    const placed: { x: number; y: number; z: number; el: HTMLElement; wpx: number; talking: boolean; mine: boolean; lg: boolean }[] = [];
    const now = performance.now();
    for (const hero of heroes) {
      const p = positions.get(hero.id);
      if (!p || hero.downTicks > 0) continue;
      seen.add(hero.pid);
      let el = this.tags.get(hero.pid);
      if (!el) {
        el = h(`span.name-tag${hero.pid === this.me() ? '.me' : ''}`, {}, h('span.say'), h('span.nm'));
        this.el.append(el);
        this.tags.set(hero.pid, el);
      }
      const text = this.label(hero);
      const lg = hero.control === 'merc' ? null : this.legacy(hero);
      const nm = el.lastElementChild as HTMLElement;
      const was = `${text}|${lg?.title ?? ''}`;
      if (this.shown.get(hero.pid) !== was) {
        this.shown.set(hero.pid, was);
        clear(nm);
        if (lg) nm.append(nameChip(text, lg));
        else nm.textContent = text;
        el.classList.toggle('lg', !!lg);
      }
      const said = this.said.get(hero.pid);
      const talking = !!said && said.until > now;
      if (said && !talking) this.said.delete(hero.pid);
      const bubble = el.firstElementChild as HTMLElement;
      const line = talking ? said.text : '';
      if (bubble.textContent !== line) bubble.textContent = line;
      el.classList.toggle('talking', talking);
      el.classList.toggle('ghost', hero.control === 'ghost');
      el.classList.toggle('merc', hero.control === 'merc');
      this.v.copy(p).setY(p.y + 1.45).project(camera);
      if (this.v.z > 1) {
        setStyle(el, 'display', 'none');
        continue;
      }
      placed.push({ x: (this.v.x * 0.5 + 0.5) * w, y: (-this.v.y * 0.5 + 0.5) * ht, z: this.v.z, el, wpx: text.length * 7 + (lg ? 34 : 10), talking, mine: hero.pid === this.me(), lg: !!lg });
    }
    // Yours first (never lost in a crowd), then who is talking, then a legacy's, then nearer first; a tag that would
    // sit on top of one already shown is hidden.
    placed.sort((a, b) => Number(b.mine) - Number(a.mine) || Number(b.talking) - Number(a.talking) || Number(b.lg) - Number(a.lg) || a.z - b.z);
    const shown: { x: number; y: number; wpx: number }[] = [];
    this.boxes.length = 0;
    for (const it of placed) {
      // Pulled back (z close to 1), names get smaller.
      const scale = Math.max(0.7, Math.min(1, (1 - it.z) * 40));
      const clash = shown.some((o) => Math.abs(o.x - it.x) < (o.wpx + it.wpx) * 0.5 * scale && Math.abs(o.y - it.y) < 16);
      if (clash) {
        setStyle(it.el, 'display', 'none');
        continue;
      }
      shown.push(it);
      // Anchored at its bottom centre: a plate with its title stands taller than a plain name.
      const half = it.wpx * 0.5 * scale;
      this.boxes.push({ x0: it.x - half, y0: it.y - (it.lg ? 36 : 20) * scale, x1: it.x + half, y1: it.y, el: it.el });
      setStyle(it.el, 'display', '');
      // Pulled back, a legacy's plate keeps its emblem and the name; its title comes back close up.
      if (it.lg) it.el.classList.toggle('far', scale < 0.9);
      setStyle(it.el, 'transform', `translate(${it.x.toFixed(1)}px, ${it.y.toFixed(1)}px) translate(-50%, -100%) scale(${scale.toFixed(2)})`);
    }
    for (const [pid, el] of this.tags) {
      if (seen.has(pid)) continue;
      el.remove();
      this.tags.delete(pid);
      this.said.delete(pid);
      this.shown.delete(pid);
    }
  }
}
